import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
  Skeleton,
  Autocomplete,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Plus,
  Eye,
  Trash2,
  X,
  Printer,
} from 'lucide-react';
import {
  createPurchaseOrder,
  getPurchaseOrders,
  deletePurchaseOrder,
  cancelPurchaseOrder,
  getPurchaseOrderStats
} from '../firebase/purchaseOrderService';
import { getSuppliers } from '../firebase/supplierService';
import { getInventoryItems } from '../firebase/inventoryService';
import { getGRNs } from '../firebase/grnService';
import { getBillingSettings } from '../firebase/settingsService';
import { printPurchaseOrder } from '../utils/purchaseOrderPrint';

const PurchaseOrders = ({ currentUser }) => {
  const [pos, setPOs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [grns, setGRNs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [stats, setStats] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([]);
  const [requireSignature, setRequireSignature] = useState(false);
  
  // Item form — free-type with autocomplete suggestions
  const [productName, setProductName] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [productItemCode, setProductItemCode] = useState('');
  const [quantity, setQuantity] = useState('');
  const [costPrice, setCostPrice] = useState('');

  // Build suggestion lists from inventory + GRN history
  const productSuggestions = useMemo(() => {
    const fromInventory = inventoryItems.map(i => ({ name: i.name, category: i.category || '', itemCode: i.itemCode || '' }));
    const fromGRNs = grns.flatMap(g => (g.items || []).map(i => ({ name: i.name, category: i.category || '', itemCode: i.itemCode || '' })));
    const all = [...fromInventory, ...fromGRNs];
    const seen = new Set();
    return all.filter(p => {
      if (!p.name || seen.has(p.name.toLowerCase())) return false;
      seen.add(p.name.toLowerCase());
      return true;
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [inventoryItems, grns]);

  const categorySuggestions = useMemo(() => {
    const cats = new Set([
      ...inventoryItems.map(i => i.category),
      ...grns.flatMap(g => (g.items || []).map(i => i.category)),
    ].filter(Boolean));
    return [...cats].sort();
  }, [inventoryItems, grns]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    
    // Load purchase orders
    const posResult = await getPurchaseOrders();
    if (posResult.success) {
      setPOs(posResult.data);
    }
    
    // Load suppliers
    const suppliersResult = await getSuppliers({ status: 'active' });
    if (suppliersResult.success) {
      setSuppliers(suppliersResult.data);
    }
    
    // Load products from inventory + GRNs
    const [productsResult, grnsResult] = await Promise.all([getInventoryItems(), getGRNs()]);
    if (productsResult.success) setInventoryItems(productsResult.data);
    if (grnsResult.success) setGRNs(grnsResult.data);
    
    // Load stats
    const statsResult = await getPurchaseOrderStats();
    if (statsResult.success) {
      setStats(statsResult.data);
    }
    
    setLoading(false);
  };

  const handleOpenDialog = () => {
    setSelectedSupplier(null);
    setExpectedDeliveryDate('');
    setNotes('');
    setItems([]);
    setRequireSignature(false);
    setProductName('');
    setProductCategory('');
    setProductItemCode('');
    setQuantity('');
    setCostPrice('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleAddItem = () => {
    if (!productName.trim() || !quantity || !costPrice) {
      setSnackbar({ open: true, message: 'Please fill product name, quantity and cost price', severity: 'error' });
      return;
    }
    const newItem = {
      productName: productName.trim(),
      category: productCategory.trim() || 'Uncategorized',
      itemCode: productItemCode.trim(),
      quantity: parseInt(quantity),
      costPrice: parseFloat(costPrice),
      subtotal: parseInt(quantity) * parseFloat(costPrice),
    };
    setItems([...items, newItem]);
    setProductName('');
    setProductCategory('');
    setProductItemCode('');
    setQuantity('');
    setCostPrice('');
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleCreatePO = async () => {
    if (submitting) return;
    if (!selectedSupplier) { setSnackbar({ open: true, message: 'Please select a supplier', severity: 'error' }); return; }
    if (items.length === 0) { setSnackbar({ open: true, message: 'Please add at least one item', severity: 'error' }); return; }
    setSubmitting(true);
    const totalAmount = items.reduce((sum, item) => sum + item.subtotal, 0);
    const poData = { supplierId: selectedSupplier.id, supplierName: selectedSupplier.name, items, totalAmount, expectedDeliveryDate: expectedDeliveryDate ? new Date(expectedDeliveryDate) : null, notes, requireSignature };
    const result = await createPurchaseOrder(poData, currentUser);
    setSubmitting(false);
    if (result.success) {
      setSnackbar({ open: true, message: `Purchase order ${result.poNumber} created successfully`, severity: 'success' });
      handleCloseDialog();
      loadData();
    } else {
      setSnackbar({ open: true, message: result.error, severity: 'error' });
    }
  };

  const handleViewPO = (po) => {
    setSelectedPO(po);
    setViewDialog(true);
  };

  const handlePrintPO = async (po) => {
    try {
      const settingsResult = await getBillingSettings();
      const settings = settingsResult.success ? settingsResult.data : {};
      // Enrich PO with creator name for the print
      const enrichedPO = { ...po };
      await printPurchaseOrder(enrichedPO, settings);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to open print dialog', severity: 'error' });
    }
  };

  const [actionConfirm, setActionConfirm] = useState(null); // { po, type: 'delete'|'cancel' }

  const handleDeletePO = (po) => setActionConfirm({ po, type: 'delete' });
  const handleCancelPO = (po) => setActionConfirm({ po, type: 'cancel' });

  const confirmAction = async () => {
    const { po, type } = actionConfirm;
    setActionConfirm(null);
    const result = type === 'delete'
      ? await deletePurchaseOrder(po.id, currentUser)
      : await cancelPurchaseOrder(po.id, currentUser);
    if (result.success) {
      setSnackbar({ open: true, message: `Purchase order ${type === 'delete' ? 'deleted' : 'cancelled'} successfully`, severity: 'success' });
      loadData();
    } else {
      setSnackbar({ open: true, message: result.error, severity: 'error' });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'partially_received': return 'info';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'partially_received': return 'Partially Received';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Purchase Orders
        </Typography>
        <Button
          variant="contained"
          startIcon={<Plus size={20} />}
          onClick={handleOpenDialog}
        >
          Create PO
        </Button>
      </Box>

      {/* Summary Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total POs
                </Typography>
                <Typography variant="h4">{stats.totalPOs}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Pending
                </Typography>
                <Typography variant="h4" color="warning.main">{stats.pendingPOs}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Completed
                </Typography>
                <Typography variant="h4" color="success.main">{stats.completedPOs}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Value
                </Typography>
                <Typography variant="h4">
                  LKR {stats.totalValue.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* POs Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>PO Number</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Order Date</TableCell>
              <TableCell>Expected Delivery</TableCell>
              <TableCell align="right">Total Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>
                    No purchase orders found. Create your first PO to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              pos.map((po) => (
                <TableRow key={po.id} hover>
                  <TableCell>
                    <Typography fontWeight={600}>{po.poNumber}</Typography>
                  </TableCell>
                  <TableCell>{po.supplierName}</TableCell>
                  <TableCell>
                    {po.orderDate?.toDate().toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {po.expectedDeliveryDate?.toDate().toLocaleDateString() || '-'}
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={600}>
                      LKR {po.totalAmount.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getStatusLabel(po.status)} 
                      color={getStatusColor(po.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleViewPO(po)} color="primary">
                      <Eye size={18} />
                    </IconButton>
                    <IconButton size="small" onClick={() => handlePrintPO(po)} title="Print PO">
                      <Printer size={18} />
                    </IconButton>
                    {po.status === 'pending' && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => handleCancelPO(po)}
                          color="warning"
                        >
                          <X size={18} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeletePO(po)}
                          color="error"
                        >
                          <Trash2 size={18} />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create PO Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>Create Purchase Order</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* Supplier Selection */}
            <Autocomplete
              options={suppliers}
              getOptionLabel={(option) => option.name}
              value={selectedSupplier}
              onChange={(e, newValue) => setSelectedSupplier(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Select Supplier" required />
              )}
            />

            {/* Expected Delivery Date */}
            <TextField
              label="Expected Delivery Date"
              type="date"
              value={expectedDeliveryDate}
              onChange={(e) => setExpectedDeliveryDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            {/* Notes */}
            <TextField
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={2}
              fullWidth
            />

            <FormControlLabel
              control={
                <Switch
                  checked={requireSignature}
                  onChange={(e) => setRequireSignature(e.target.checked)}
                />
              }
              label="Require authorization signature on printed PO"
            />

            {/* Add Items Section */}
            <Typography variant="h6" sx={{ mt: 2 }}>Add Items</Typography>
            
            <Grid container spacing={2}>
              {/* Row 1: Product Name, Category, Item Code */}
              <Grid item xs={12} md={5}>
                <Autocomplete
                  freeSolo
                  options={productSuggestions.map(p => p.name)}
                  value={productName}
                  onInputChange={(_, val) => {
                    setProductName(val);
                    const match = productSuggestions.find(p => p.name.toLowerCase() === val.toLowerCase());
                    if (match?.category) setProductCategory(match.category);
                    if (match?.itemCode) setProductItemCode(match.itemCode);
                    const invMatch = inventoryItems.find(i => i.name.toLowerCase() === val.toLowerCase());
                    if (invMatch?.costPrice) setCostPrice(String(invMatch.costPrice));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Product Name *" placeholder="Type or select a product" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  freeSolo
                  options={categorySuggestions}
                  value={productCategory}
                  onInputChange={(_, val) => setProductCategory(val)}
                  renderInput={(params) => (
                    <TextField {...params} label="Category" placeholder="e.g. Perfume" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  freeSolo
                  options={productSuggestions.filter(p => p.itemCode).map(p => p.itemCode)}
                  value={productItemCode}
                  onInputChange={(_, val) => {
                    setProductItemCode(val);
                    // Auto-fill from item code
                    const match = productSuggestions.find(p => p.itemCode && p.itemCode.toLowerCase() === val.toLowerCase());
                    if (match) {
                      setProductName(match.name);
                      if (match.category) setProductCategory(match.category);
                      const invMatch = inventoryItems.find(i => i.name.toLowerCase() === match.name.toLowerCase());
                      if (invMatch?.costPrice) setCostPrice(String(invMatch.costPrice));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Item Code" placeholder="e.g. PRF-001" />
                  )}
                />
              </Grid>
              {/* Row 2: Qty, Cost, Add button */}
              <Grid item xs={6} md={3}>
                <TextField
                  label="Qty *"
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  label="Cost (LKR) *"
                  type="number"
                  value={costPrice}
                  onChange={(e) => setCostPrice(e.target.value)}
                  fullWidth
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
                <Button variant="outlined" onClick={handleAddItem} sx={{ height: 56 }}>
                  + Add to Order
                </Button>
              </Grid>
            </Grid>

            {/* Items List */}
            {items.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>Item Code</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Cost</TableCell>
                      <TableCell align="right">Subtotal</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{item.itemCode || '—'}</Typography>
                        </TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">LKR {item.costPrice.toLocaleString()}</TableCell>
                        <TableCell align="right">LKR {item.subtotal.toLocaleString()}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveItem(index)}
                            color="error"
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={4} align="right">
                        <Typography fontWeight={600}>Total:</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={600}>
                          LKR {items.reduce((sum, item) => sum + item.subtotal, 0).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>Cancel</Button>
          <Button onClick={handleCreatePO} variant="contained" disabled={submitting}>
            {submitting ? 'Creating...' : 'Create PO'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View PO Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Purchase Order Details</DialogTitle>
        <DialogContent>
          {selectedPO && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">PO Number</Typography>
                  <Typography variant="body1" fontWeight={600}>{selectedPO.poNumber}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Supplier</Typography>
                  <Typography variant="body1" fontWeight={600}>{selectedPO.supplierName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Order Date</Typography>
                  <Typography variant="body1">
                    {selectedPO.orderDate?.toDate().toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip label={getStatusLabel(selectedPO.status)} color={getStatusColor(selectedPO.status)} size="small" />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Signature Required</Typography>
                  <Typography variant="body1">{selectedPO.requireSignature ? 'Yes' : 'No'}</Typography>
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Items</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>Item Code</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Cost Price</TableCell>
                      <TableCell align="right">Subtotal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedPO.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{item.itemCode || '—'}</Typography>
                        </TableCell>
                        <TableCell>{item.category}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">LKR {item.costPrice.toLocaleString()}</TableCell>
                        <TableCell align="right">LKR {item.subtotal.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={5} align="right">
                        <Typography fontWeight={600}>Total:</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={600}>
                          LKR {selectedPO.totalAmount.toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {selectedPO.notes && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">Notes</Typography>
                  <Typography variant="body1">{selectedPO.notes}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Close</Button>
          <Button variant="contained" startIcon={<Printer size={16} />} onClick={() => handlePrintPO(selectedPO)}>
            Print / Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      {/* Action Confirm Dialog */}
      <Dialog open={!!actionConfirm} onClose={() => setActionConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{actionConfirm?.type === 'delete' ? 'Delete PO?' : 'Cancel PO?'}</DialogTitle>
        <DialogContent>
          <Typography>
            {actionConfirm?.type === 'delete'
              ? `Delete ${actionConfirm?.po?.poNumber}? This cannot be undone.`
              : `Cancel ${actionConfirm?.po?.poNumber}? This cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setActionConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmAction}>Confirm</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PurchaseOrders;
